import { useEffect, useState } from "react";
import {
  getApprovedAttorneys,
  uploadInmateDocument,
} from "../api/inmateUploads";
import InmateNav from "../components/InmateNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";
import { DEFAULT_DOCUMENT_POLICY, validateDocumentSelection } from "../utils/documentPolicy";
import { getRegistrationConfiguration } from "../api/registration";

export default function InmateUpload() {
  const [attorneys, setAttorneys] = useState([]);
  const [attorneyProfileId, setAttorneyProfileId] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [policy, setPolicy] = useState(DEFAULT_DOCUMENT_POLICY);

  const loadAttorneys = async () => {
    setLoading(true);

    try {
      const res = await getApprovedAttorneys();
      const list = res.data.data || [];

      setAttorneys(list);

      if (list.length === 1) {
        setAttorneyProfileId(String(list[0].attorney_profile_id));
      }
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load approved attorneys."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttorneys();
    getRegistrationConfiguration().then((response) => setPolicy({ ...DEFAULT_DOCUMENT_POLICY, ...(response.data?.document_policy || {}) })).catch(() => {});
  }, []);

  const chooseFile = (event) => {
    const selected = event.target.files[0] || null;
    const selectionError = validateDocumentSelection(selected, policy);
    if (selectionError) { setFile(null); setError(selectionError); event.target.value = ''; return; }
    setError(''); setFile(selected);
  };

  const submit = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    if (!attorneyProfileId || !title || !file) {
      setError("Attorney, title, and PDF file are required.");
      return;
    }

    const formData = new FormData();
    formData.append("attorney_profile_id", attorneyProfileId);
    formData.append("title", title);
    formData.append("file", file);

    setUploading(true);

    try {
      const response = await uploadInmateDocument(formData);

      setMessage(response?.data?.message || "Document uploaded and queued for security scanning.");
      setTitle("");
      setFile(null);

      const input = document.getElementById("inmate-file-upload");
      if (input) {
        input.value = "";
      }
    } catch (err) {
      setError(getErrorMessage(err, "Unable to upload document."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Send Document to Attorney</div>
          </div>
        </div>
	<InmateNav />
      </div>

      <main className="page-wrap">
        <div className="mb-4">
          <h2 className="mb-1">Send Document</h2>
          <p className="text-muted mb-0">
            Send a privileged legal PDF to an approved attorney.
          </p>
        </div>

        <StatusMessage type="success">{message}</StatusMessage>
        <StatusMessage type="warning">{error}</StatusMessage>

        <div className="portal-card">
          <div className="portal-card-body">
            {loading ? (
              <div className="text-muted">Loading approved attorneys...</div>
            ) : attorneys.length === 0 ? (
              <div className="text-muted">
                No approved attorneys are available yet.
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="mb-3">
                  <label className="form-label">Attorney</label>
                  <select
                    className="form-select form-select-lg"
                    value={attorneyProfileId}
                    onChange={(e) => setAttorneyProfileId(e.target.value)}
                    required
                  >
                    <option value="">Select attorney</option>

                    {attorneys.map((item) => (
                      <option
                        key={item.attorney_profile_id}
                        value={item.attorney_profile_id}
                      >
                        {item.attorney_name} — {item.law_firm_name || "Law Firm"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Document Title</label>
                  <input
                    className="form-control form-control-lg"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength="255"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label">PDF File</label>
                  <input
                    id="inmate-file-upload"
                    type="file"
                    className="form-control form-control-lg"
                    accept="application/pdf"
                    onChange={chooseFile}
                    aria-describedby="inmate-document-file-help"
                    required
                  />
                  <div className="form-text" id="inmate-document-file-help">
                    <strong>{policy.disclosure}</strong>{policy.security_review_enforced ? ' The document will be delivered only after security scanning succeeds.' : ''}
                  </div>
                </div>

                <AsyncButton className="btn btn-dark btn-lg" busy={uploading} busyText="Sending securely…">
                  Send to Attorney
                </AsyncButton>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
